import {
  Button, Col,
  ConfigProvider,
  Dropdown,
  Form,
  Menu,
  Modal,
  notification,
  Row,
  Table,
  Tag
} from 'antd';
import {
  EditOutlined, ExclamationCircleOutlined, FilterOutlined,
  MoreOutlined,
  RetweetOutlined,
  RollbackOutlined,
  SwapOutlined
} from '@ant-design/icons';
import trTR from 'antd/es/locale/tr_TR';
import React, { lazy, Suspense, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import userHaveRolePermission from '../../../../helpers/userHaveRolePermission';
import { PAGE_URLS } from '../../../../helpers/endpoints';
import { useTranslation } from 'react-i18next';
import {
  COURIER_CHANGE_STATES,
  ORDER_STATUS_COLORS,
  ORDER_STATUSES, ORDER_TYPES,
} from '../../../../helpers/constants';
import { cancelOrderManually, deliverOrderManually, notDeliverOrderManually } from '../../../../store/requests/order';
import { getCourier } from '../../../../store/requests/courier';
import { getOrdersWaitingTransfer } from '../../../../store/requests/order';
import moment from 'moment';
import { useSelector } from 'react-redux';
const TableFilters = lazy(() => import('./TableFilter'));
const OrderTransfer = lazy(() => import('../../../../components/Orders/OrderTransfer'));
const ChangeOrderCourier = lazy(() => import('../../../../components/Orders/ChangeOrderCourier'));
const Edit = lazy(() => import('../../../../components/Orders/Edit'));
const { confirm } = Modal;

const initialPageParams = {
  order: 'descend',
  column: {
    dataIndex: 'order.deliveryDate',
  },
  pagination: {
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total) => `Toplam ${total} kayıt`,
    pageSizeOptions: [20, 30, 50],
    size: 'small',
    current: 1,
    pageSize: 20
  }
};
const OrdersWaitingTransfer = ({ route }) => {
  const history = useHistory();
  const {
    isViewEnabled,
    isCrudEnabled,
    changeCourier,
    changeStatus,
    edit,
    orderTransfer
  } = userHaveRolePermission(route?.name);
  if (!isViewEnabled) {
    history.push(`${PAGE_URLS.AUTH}${PAGE_URLS.CONTROL}`);
  }
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [getCourierLoading, setGetCourierLoading] = useState(false);
  const [openOrderTransferModal, setOpenOrderTransferModal] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [couriersOfZone, setCouriersOfZone] = useState(null);
  const [filteringValue, setFilteringValue] = useState({});
  const [selectedStoreName, setSelectedStoreName] = useState('');
  const activeStoreId = useSelector(
    (state) => state.auth?.user.activeStore?.id,
  );
  const activeRegionId = useSelector(
    (state) => state.auth?.user.activeRegion?.id,
  );
  const [isChangeCourierModalOpen, setIsChangeCourierModalOpen] = useState(false);
  const [orderNumberForTransfer, setOrderNumberForTransfer] = useState(null);
  const [rows, setRows] = useState([]);
  const [tableParams, setTableParams] = useState(initialPageParams);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);

  useEffect(() => {
    (() => {
      fetchData(tableParams, filteringValue);
    })();
  }, []);

  const getQueryString = (tableParamsValue, filters) => {
    let queryString = null;

    if (filters?.country) {
      queryString = `filter=order.slot.zone.store.region.country.id==1`;
    } else {
      queryString = activeStoreId
        ? `filter=order.slot.zone.store.id==${activeStoreId}`
        : `filter=order.slot.zone.store.region.id==${activeRegionId}`;
    }

    if (Object.values(filters).some((item) => !!item)) {
      Object.keys(filters).forEach((item, idx) => {
        if (filters[item] && item !== 'country') {
          const partnerMappings = {
            'ISTEGELSIN': ` and order.${item}==${filters[item]}`,
            'SOK_POSITIVE': ` and order.${item}==SOK and channel==POSITIVE`,
            'SOK_ECOM': ` and order.${item}==SOK and channel==TRANSFORMATION`,
          };

          queryString += partnerMappings[filters[item]] || '';

          if (item !== 'channel') {
            queryString += ` and ${item === 'status' ? 'order.status' : `order.${item}`}${item === 'externalCreateTime=ge' || item === 'externalCreateTime=le' ? '=' : '=='}${item === 'address.text' ? ` ${filters[item]}*` : filters[item]}`;
          }
        }
      });
    }
    queryString = `${queryString}&page=${tableParamsValue.pagination.current - 1}&size=${tableParamsValue.pagination.pageSize}`;

    if (tableParamsValue.column) {
      queryString = `${queryString}&sort=${tableParamsValue.column.dataIndex === 'slot' ? 'slot.zone.store.code' : tableParamsValue.column.dataIndex},${ORDER_TYPES[tableParamsValue.order]}`;
    }

    return queryString;
  };

  const fetchData = (tableParamsValue, filter) => {
    const queryString = getQueryString(tableParamsValue, filter);
    loadDataWithDebounce(queryString);
  };

  const loadDataWithDebounce = (queryString) =>  {
    setLoading(true);
    getOrdersWaitingTransfer(queryString)
      .then((res) => {
        const { content, page } = res;
        setRows(content ?? []);
        setTableParams({
          ...tableParams,
          pagination: {
            total: page.totalElements,
            current: page.number + 1,
            pageSize: page.size
          }
        });
      })
      .finally(() => setLoading(false));
  }

  const columns = [
    {
      dataIndex: 'order.customerOrderNumber',
      title: t('orderId'),
      sorter: true,
      showSorterTooltip: false,
      render: (_, items) => items?.order?.customerOrderNumber ?? '--'
    },
    {
      dataIndex: 'order.partner',
      title: t('orderChannel'),
      sorter: true,
      showSorterTooltip: false,
      render: (_, items) => {
        const orderChannel = {
          POSITIVE: 'ŞOK-Positive',
          TRANSFORMATION: 'ŞOK-Yeni Ecom'
        };
        return orderChannel[items?.order?.channel] ?? items?.order?.partner;
      }
    },
    {
      dataIndex: 'order.status',
      title: t('shipmentStatus'),
      sorter: true,
      showSorterTooltip: false,
      render: (_,items) => (
        items?.order?.status ? <Tag color={ORDER_STATUS_COLORS[items?.order?.status]}>{t(items?.order?.status)}</Tag> : '--'
      )
    },
    {
      dataIndex: 'order.slotStartTime',
      title: t('slotTime'),
      sorter: true,
      showSorterTooltip: false,
      render: (_, items) => items?.order?.slot
        ? `${items?.order?.slot.startTime} - ${items?.order?.slot.endTime}`
        : '--'
    },
    {
      dataIndex: 'order.address.text',
      title: t('address'),
      sorter: true,
      showSorterTooltip: false,
      render: (_,items) => items?.order?.address
        ? items?.order?.address?.text
        : '--'
    },
    {
      dataIndex: 'order.createdAt',
      title: t('orderCreatedTime'),
      sorter: true,
      showSorterTooltip: false,
      render: (_, items) => moment(items?.order?.externalCreateTime).format('DD/MM/YYYY')
    },
    {
      dataIndex: 'order.deliveryDate',
      title: t('deliveryDate'),
      sorter: true,
      showSorterTooltip: false,
      render: (_, items) => moment(items?.order?.deliveryDate).format('DD/MM/YYYY')
    },
    {
      dataIndex: 'order.slot.zone.store.code',
      title: t('store'),
      sorter: true,
      showSorterTooltip: false,
      render: (_, items) => items?.order?.slot?.zone?.store
        ? `${items?.order?.slot?.zone?.store?.code} - ${items?.order?.slot?.zone?.store?.name}`
        : '--'
    },
    {
      width: 50,
      render: (dataItem) =>
        isCrudEnabled && (
          <Dropdown
            overlay={
              <Menu>
                {edit && <Menu.Item
                  key="edit"
                  icon={<EditOutlined />}
                  onClick={() => {
                    setIsEditModalOpen(true);
                  }}
                >
                  {t('goToDetail')}
                </Menu.Item>}
                {/*{changeCourier && <Menu.Item
                  key="changeCourier"
                  icon={<SwapOutlined />}
                  onClick={() => {
                    changeOrderCourier(
                      dataItem.order.slot.zone.id,
                      dataItem.order.status
                    );
                  }}
                >
                  {t('changeCourier')}
                </Menu.Item>}
                {changeStatus && (
                  <Menu.SubMenu
                    key="changeStatus"
                    disabled={[
                      ORDER_STATUSES.CANCELLED,
                      ORDER_STATUSES.DELIVERED,
                      ORDER_STATUSES.NOT_DELIVERED,
                      ORDER_STATUSES.COMPLETED,
                      ORDER_STATUSES.WAITING_FOR_APPROVAL,
                      ORDER_STATUSES.ARRIVED_AT_STORE,
                      ORDER_STATUSES.COLLECTING
                    ].includes(dataItem.order.status)}
                    title={
                      <span>
                        <RetweetOutlined /> {t('changeStatus')}
                      </span>
                    }
                  >
                    <>
                      {createMenuItems(
                        dataItem.order,
                        ORDER_STATUSES.CANCELLED,
                        'CANCELLED'
                      )}
                      {createMenuItems(
                        dataItem.order,
                        ORDER_STATUSES.DELIVERED,
                        'DELIVERED'
                      )}
                      {createMenuItems(
                        dataItem.order,
                        ORDER_STATUSES.NOT_DELIVERED,
                        'NOT_DELIVERED'
                      )}
                    </>
                  </Menu.SubMenu>
                )}*/}
                {orderTransfer && (
                  <Menu.Item
                    key="orderTransfer"
                    icon={<RollbackOutlined />}
                    disabled={
                      dataItem.order.status !== ORDER_STATUSES.NEW_ORDER
                    }
                    onClick={() => {
                      orderTransferFn(dataItem.order.orderNumber);
                    }}
                  >
                    {t('orderTransfer')}
                  </Menu.Item>
                )}
              </Menu>
            }
            placement="bottomRight"
            trigger={['click']}
            arrow
            onClick={() => setSelectedItemId(dataItem.order.id)}
          >
            <Button shape="circle" icon={<MoreOutlined />} />
          </Dropdown>
        )
    }
  ];

  const orderTransferFn = (orderNumber) => {
    setOpenOrderTransferModal(true);
    setOrderNumberForTransfer(orderNumber);
  };

  const changeOrderCourier = (orderZone, orderStatus) => {
    if (!COURIER_CHANGE_STATES.includes(orderStatus)) {
      notification.warn({
        message: t('warning'),
        description: t('statusOfTheOrderIsNotSuitableForChangingTheCourier')
      });
      return;
    }

    setIsChangeCourierModalOpen(true);

    if (orderZone) {
      let queryString = `filter=user.roles.active==true and user.roles.zone.id==${orderZone} and active==true`;
      setGetCourierLoading(true);
      getCourier(queryString)
        .then((courier) => {
          const { _embedded, page } = courier;
          const { courierResponseDtoList } = _embedded ? _embedded : [];
          setCouriersOfZone(courierResponseDtoList ?? []);
        })
        .finally(() => setGetCourierLoading(false));
    }
  };

  // TODO: Not used for now, will be added later according to customer demand
  const createMenuItems = (dataItem, statusKey, statusText) => (
    <Menu.Item
      key={statusKey}
      onClick={() => {
        showConfirmForOrderChangeStatus(dataItem.id, statusKey);
      }}
    >
      {t(statusText)}
    </Menu.Item>
  );

  const showConfirmForOrderChangeStatus = (id, status) => {
    const statusTranslate = t(status);
    const confirmationMessage = t('doYouConfirmChangeOrderStatus', {
      statusTranslate
    });

    const actionMap = {
      [ORDER_STATUSES.CANCELLED]: cancelOrderManually,
      [ORDER_STATUSES.DELIVERED]: deliverOrderManually,
      [ORDER_STATUSES.NOT_DELIVERED]: notDeliverOrderManually
    };

    const action = actionMap[status];

    confirm({
      title: t('doYouConfirm'),
      icon: <ExclamationCircleOutlined />,
      content: confirmationMessage,
      okText: t('ok'),
      cancelText: t('cancel'),
      centered: true,
      onOk() {
        action(id, status).then((res) => {
          if (res) {
            notificationChangeStatus('success');
            fetchData(initialPageParams, filteringValue);
          }
        });
      },
      onCancel() {
      }
    });
  };
  const notificationChangeStatus = (type) => {
    notification[type]({
      message: t('processCompleted'),
      description: t('orderStatusChanged')
    });
  };

  const removeItemFromFilters = (destinationItem) => {
    Object.keys(filteringValue).forEach((item) => {
      if (item === destinationItem) {
        const editedFilters = {
          ...filteringValue,
          [destinationItem]: undefined
        };
        setFilteringValue(editedFilters);
        form.setFieldsValue(editedFilters);
        fetchData(initialPageParams, editedFilters);
        setRows([]);
      }
    });
  };

  const renderSelectedFilters = () => {
    const fieldLocalizationVars = {
      customerOrderNumber: 'orderId',
      status: 'shipmentStatus',
      'address.neighborhood': 'neighborhood',
      'address.text': 'address',
      'externalCreateTime=ge': 'externalCreateTimeGe',
      'externalCreateTime=le': 'externalCreateTimeLe',
      deliveryDate: 'deliveryDate',
      timeSlot: 'slotTime',
      'slot.zone.store.id': 'store',
      country: 'acrossTurkey',
      channel: 'orderChannel',
    };

    return Object.keys(filteringValue).map((item, idx) => {
      if (filteringValue[item]) {
        return (
          <Tag
            key={idx}
            closable
            color="geekblue"
            onClose={(e) => {
              e.preventDefault();
              removeItemFromFilters(item);
            }}
          >
            {`${t(fieldLocalizationVars[item])}
              : ${item === 'timeSlot' ? filteringValue[item] : item === 'externalCreateTime=ge' || item === 'externalCreateTime=le' ? filteringValue[item].split('T')[0] : item === 'slot.zone.store.id' ? selectedStoreName : t(filteringValue[item])}`}
          </Tag>
        );
      }
    });
  };

  const handleTableChange = (pagination, filters1, sorter) => {
    fetchData(
      {
        pagination,
        ...sorter
      },
      filteringValue
    );
  };

  return (
    <div>
      <Row
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
        }}
      >
        <Col>
          <Button
            shape="round"
            icon={<FilterOutlined />}
            onClick={() => setIsFiltersOpen(!isFiltersOpen)}
          >
            {t('filters')}
          </Button>
        </Col>
      </Row>
      <Row
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginTop: '10px',
        }}
      >
        {renderSelectedFilters()}
      </Row>
      <Row style={{ marginTop: '10px' }}>
        <ConfigProvider locale={trTR}>
          <Table
            style={{ width: '100%' }}
            size="small"
            columns={columns}
            dataSource={rows}
            pagination={tableParams.pagination}
            loading={loading}
            onChange={handleTableChange}
            rowKey={(record) =>
              `${record.order.id}`
            }
            scroll={{
              y: '70vh'
            }}
          />
        </ConfigProvider>
      </Row>
      <Suspense fallback={<></>}>
        {isFiltersOpen && (
          <TableFilters
            setIsFiltersOpen={setIsFiltersOpen}
            handlerFilter={(filter) => {
              setFilteringValue(filter);
              fetchData(initialPageParams, filter);
            }}
            form={form}
            setSelectedStoreName={setSelectedStoreName}
          />
        )}
        {isEditModalOpen && (
          <Edit
            orderId={selectedItemId}
            onCloseHandler={() => setIsEditModalOpen(false)}
          />
        )}
        {isChangeCourierModalOpen && (
          <ChangeOrderCourier
            orderId={selectedItemId}
            couriers={couriersOfZone}
            onCloseHandler={() => setIsChangeCourierModalOpen(false)}
            onSuccessCallback={() => {
              setIsChangeCourierModalOpen(false);
              fetchData(initialPageParams, filteringValue);
            }}
            getCourierLoading={getCourierLoading}
          />
        )}
        {openOrderTransferModal && (
          <OrderTransfer
            orderNumber={orderNumberForTransfer}
            onSuccessCalback={() => {
              setOpenOrderTransferModal(false);
              fetchData(initialPageParams, filteringValue);
            }}
            onCloseHandler={() => {
              setOpenOrderTransferModal(false);
            }}
          />
        )}
      </Suspense>
    </div>
  );
};

export default OrdersWaitingTransfer;
